import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import _ from "underscore";
import PropTypes from "prop-types";
import ReservationCalendar from "components/source/shared/calendars/reservation-calendar";
import AnimatedZipInput from "components/source/shared/animated_inputs/animated-zip-input";
import deliveryCutoffActions from "actions/delivery-cutoff-actions";
import { onEnterPress } from "helpers/a11y-helper";
import AtomLinkButton from "components/source/atoms/atom-link-button";
import constants from "rtr-constants";
import { logReservationAction } from "helpers/reservation-pixel-helper";
import { productPropType, reservationPropType } from "../../../propTypes";
import {
  flagsAndExperimentNames,
  flagsAndExperimentsPropType,
  withFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";

export class DatepickerWithZipAndDuration extends React.Component {
  static propTypes = {
    date: PropTypes.string,
    dateInputName: PropTypes.string.isRequired,
    dateOptions: PropTypes.object.isRequired,
    deliveryCutoffs: PropTypes.array,
    duration: PropTypes.number,
    durationInputName: PropTypes.string.isRequired,
    errors: PropTypes.object,
    fetchBlackoutDays: PropTypes.func.isRequired,
    fetchDeliveryCutoffs: PropTypes.func.isRequired,
    flagsAndExperiments: flagsAndExperimentsPropType,
    hideDurationFilter: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    product: productPropType,
    reservation: reservationPropType,
    updateZipValidity: PropTypes.func,
    zipCode: PropTypes.string,
    zipInputName: PropTypes.string.isRequired,
  };

  className = "date-zip-picker";
  state = {
    zipError: "",
  };
  rentalDurations = {
    fourDay: 4,
    eightDay: 8,
  };
  zipError = "Enter ZIP code";

  componentDidMount() {
    const { deliveryCutoffs, fetchDeliveryCutoffs, updateZipValidity, zipCode } = this.props;
    if (this.validateZip(zipCode)) {
      updateZipValidity(true);

      if (!deliveryCutoffs) {
        fetchDeliveryCutoffs(zipCode);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { zipCode: previousZipCode } = prevProps;
    const { deliveryCutoffs, fetchDeliveryCutoffs, updateZipValidity, zipCode } = this.props;

    if (!previousZipCode && zipCode) {
      if (this.validateZip(zipCode)) {
        updateZipValidity(true);

        if (!deliveryCutoffs) {
          fetchDeliveryCutoffs(zipCode);
        }
      }
    }
  }

  toggleDuration = () => {
    if (!this.props.onChange) {
      return;
    }
    const newDuration = this.props.duration === 4 ? 8 : 4;
    this.props.onChange(newDuration, true, this.props.durationInputName);
  };

  validateZip(zip) {
    return zip && zip.match(constants.validations.zipCode.zipCodeValidator);
  }

  onZipChange(zip, isValid) {
    const { fetchBlackoutDays, fetchDeliveryCutoffs, onChange, updateZipValidity, zipCode, zipInputName } = this.props;
    if (!onChange) {
      return;
    }
    // tell the parent component whether or not we have a valid, 5 digit zip code
    if (updateZipValidity) {
      updateZipValidity(isValid);
    }
    // don't set the error state to invalid while a user is still inputting a value
    this.setState({ zipError: "" });

    if (isValid && !_.isEqual(zipCode, zip)) {
      fetchDeliveryCutoffs(zip);
      fetchBlackoutDays(zip);
    }
    onChange(zip, isValid, zipInputName);
  }

  onZipBlur(zip, isValid) {
    if (zip && !isValid) {
      this.setState({ zipError: constants.clientSideErrorMessages.formValidation.zipCode });
    }
  }

  onDateChange(dateObj) {
    if (!this.props.onChange) {
      return;
    }
    logReservationAction("rezo_select", this.props.reservation, this.props.product);

    this.props.onChange(dateObj.name, true, this.props.dateInputName);
  }

  getDuration() {
    // Check that it's either four or eight days, otherwise default to 4
    const isFourOrEight = _.contains(this.rentalDurations, this.props.duration);
    return isFourOrEight && _.isNumber(this.props.duration) ? this.props.duration : this.rentalDurations.fourDay;
  }

  highlightZip = () => {
    if (this.props.zipCode) {
      return true;
    } else {
      this.setState({ zipError: this.zipError });
      return false;
    }
  };

  renderMessaging() {
    return <div className="universal-large">We recommend choosing a delivery date 2 days before your event.</div>;
  }

  renderDurationFilter() {
    if (this.props.hideDurationFilter) {
      return;
    }

    const isEightDay = this.getDuration() === this.rentalDurations.eightDay;
    const savingPercent = this.props.flagsAndExperiments[flagsAndExperimentNames.RSV_UPDATED_8_DAY_PRICING_KILLSWITCH]
      ? 10
      : 20;

    return (
      <div className={`${this.className}__switch-duration`}>
        <p>Rentals last for {this.getDuration()} days. </p>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <p>
          <AtomLinkButton
            onClick={this.toggleDuration}
            onKeyDown={onEnterPress(this.toggleDuration)}
            bold={false}
            testId={`switch-duration-to-${isEightDay ? "4" : "8"}-day`}
            role="presentation">
            {isEightDay ? "Switch to a 4" : `Save ${savingPercent}% per day with an 8`}-day rental.
          </AtomLinkButton>
        </p>
      </div>
    );
  }

  renderDateError() {
    if (!this.props?.errors?.date) {
      return;
    }
    return <div className="datepicker-date-error">{this.props.errors.date}</div>;
  }

  render() {
    return (
      <div className={this.className}>
        {this.renderMessaging()}
        <AnimatedZipInput
          id="reservation-zip-input"
          additionalClassName={`${this.className}__zip-input`}
          label="Delivery ZIP Code"
          name="zip-code"
          value={this.props.zipCode}
          error={this.props?.errors?.zipCode || this.state.zipError}
          onChangeCallback={(zip, isValid) => this.onZipChange(zip, isValid)}
          focus={!this.props.zipCode}
          required={true}
          onBlurCallback={(zip, isValid) => this.onZipBlur(zip, isValid)}
        />
        {this.renderDateError()}
        <div
          className={`${this.className}__datepicker`}
          onClick={this.highlightZip}
          onKeyDown={onEnterPress(this.highlightZip)}
          role="presentation">
          <ReservationCalendar
            error={this.props?.errors?.date}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.dateOptions}
            date={this.props.date}
            deliveryCutoffs={this.props.deliveryCutoffs}
            duration={this.getDuration()}
            idSuffix="hold-date"
            onChange={dateObj => this.onDateChange(dateObj)}
            loggingObjectType="node"
          />
        </div>
        {this.renderDurationFilter()}
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  fetchBlackoutDays: zipCode => dispatch(deliveryCutoffActions.fetchBlackoutDays(zipCode)),
  fetchDeliveryCutoffs: zipCode => dispatch(deliveryCutoffActions.fetchDeliveryCutoffs(zipCode)),
});

export default compose(
  connect(null, mapDispatchToProps),
  withFlagsAndExperiments(flagsAndExperimentNames.RSV_UPDATED_8_DAY_PRICING_KILLSWITCH)
)(DatepickerWithZipAndDuration);
